import { Injectable } from '@angular/core';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { getUserDefaultsPlugin } from './native-storage.plugin';
import { Geolocation } from '@capacitor/geolocation';

/** Result of startTracking: started = true if tracking is on; showAllowAlwaysModal = true when user must open Settings (whenInUse→Always, or denied / refus popup) */
export interface StartTrackingResult {
  started: boolean;
  showAllowAlwaysModal?: boolean;
}

export type LocationAuthorizationStatus = 'always' | 'whenInUse' | 'denied' | 'notDetermined';

// Define the plugin interface (same as Android)
export interface BackgroundLocationPlugin {
  startTracking(): Promise<void>;
  stopTracking(): Promise<void>;
  setConfig(options: { apiUrl: string; authToken: string; userRole: string; providerCurrentState?: boolean }): Promise<void>;
  setProviderCurrentState(options: { state: boolean }): Promise<void>;
  isTracking(): Promise<{ isTracking: boolean }>;
  checkBatteryOptimization(): Promise<{ isIgnoring: boolean }>;
  requestBatteryOptimizationDisable(): Promise<void>;
  getLocationAuthorizationStatus(): Promise<{ status: string }>;
  openPermissionGuide(options: { reason: string }): Promise<{ opened?: boolean }>;
  hasOverlayPermission(): Promise<{ granted: boolean }>;
  startPresencePing(): Promise<void>;
  stopPresencePing(): Promise<void>;
}

// Register the native plugin (works on both iOS and Android)
export const BackgroundLocationNativePlugin =
  registerPlugin<BackgroundLocationPlugin>('BackgroundLocation');
const BackgroundLocation = BackgroundLocationNativePlugin;

import { BehaviorSubject, Observable } from 'rxjs';
import { App } from '@capacitor/app';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './localStorage.service';
import { AppService } from './app.service';
import { RoleHelperService } from './role-helper.service';

export interface LocationUpdate {
  latitude: number;
  longitude: number;
  accuracy?: number;
  altitude?: number;
  speed?: number;
  heading?: number;
  timestamp: number;
}

/** Key set by native (AppDelegate) when app enters background with whenInUse + provider active. JS reads on foreground and shows modal. */
const SHOW_ALWAYS_MODAL_ON_FOREGROUND_KEY = 'show_always_modal_on_foreground';

const IOS_LOCATION_AUTH_UD_KEY = 'ios_location_authorization_status';

@Injectable({
  providedIn: 'root',
})
export class BackgroundLocationService {
  private isTracking = false;
  private locationSubject = new BehaviorSubject<LocationUpdate | null>(null);
  private lastSentLocation: { lat: number; lng: number } | null = null;

  /** True when app is in background (from appStateChange). */
  private inBackground = false;
  /** Count of getCurrentLocation() calls while in background in the current background session. */
  private backgroundLocationAttempts = 0;
  /** When true, show "Allow location always" modal on next foreground (after multiple location attempts in background with whenInUse). */
  private showAlwaysModalOnResume = false;

  /** Génère une commande unique pour contourner le cooldown natif (hash) */
  private buildCommandValue(cmd: 'start' | 'stop'): string {
    return `${cmd}:${Date.now()}`;
  }

  public location$: Observable<LocationUpdate | null> = this.locationSubject.asObservable();

  constructor(
    private localStorageService: LocalStorageService,
    private appService: AppService,
    private roleHelper: RoleHelperService
  ) {
    this.initializeBackgroundMode();
    this.setupAppStateListeners();
  }

  /**
   * Initialize background mode
   * Native services handle everything
   */
  private async initializeBackgroundMode() {
    if (Capacitor.getPlatform() === 'web') return;
    try {
      const { isActive } = await App.getState();
      this.inBackground = !isActive;
    } catch {
      // ignore
    }
  }

  /**
   * Setup app state listeners.
   * - When going to background: track state and reset attempt counter.
   * - When coming to foreground: show "Allow always" modal if there were multiple location attempts in background with whenInUse; or start tracking if user enabled "Always" in Settings.
   */
  private setupAppStateListeners() {
    if (Capacitor.getPlatform() === 'web') return;
    App.addListener('appStateChange', async ({ isActive }) => {
      if (!isActive) {
        this.inBackground = true;
        this.backgroundLocationAttempts = 0;
        return;
      }
      this.inBackground = false;
      const user = this.localStorageService.getUserData();
      const role = this.roleHelper.extractRoleFromUser(user?.client);
      const currentState = user?.client?.current_state;
      const isPrestataireActive = role === 'prestataire' && currentState === true;

      // Native sets this flag in applicationDidEnterBackground when provider is active and permission is whenInUse
      try {
        const ud = getUserDefaultsPlugin();
        const res = await ud.get({ key: SHOW_ALWAYS_MODAL_ON_FOREGROUND_KEY }) as { value?: string };
        if (res?.value === 'true' && isPrestataireActive) {
          await ud.set({ key: SHOW_ALWAYS_MODAL_ON_FOREGROUND_KEY, value: '' });
          const status = await this.getLocationAuthorizationStatus();
          if (status === 'whenInUse' || status === 'denied') {
            if (this.appService.canShowBackgroundLocationAlwaysModal()) {
              this.appService.dispatchModal('BACKGROUND_LOCATION_ALWAYS_MODAL', true);
            }
            return;
          }
          // If user already set "Always" in Settings, do not show modal; fall through to start tracking
        }
      } catch {
        // ignore
      }

      if (this.showAlwaysModalOnResume && isPrestataireActive) {
        this.showAlwaysModalOnResume = false;
        const status = await this.getLocationAuthorizationStatus();
        if (status === 'whenInUse' || status === 'denied') {
          if (this.appService.canShowBackgroundLocationAlwaysModal()) {
            this.appService.dispatchModal('BACKGROUND_LOCATION_ALWAYS_MODAL', true);
          }
          return;
        }
      }

      if (!isPrestataireActive || this.isTracking) return;
      const status = await this.getLocationAuthorizationStatus();
      if (status === 'always') {
        await this.doStartTracking();
      } else if (status === 'whenInUse' || status === 'denied') {
        if (this.appService.canShowBackgroundLocationAlwaysModal()) {
          this.appService.dispatchModal('BACKGROUND_LOCATION_ALWAYS_MODAL', true);
        }
      }
    });
  }

  /**
   * Request location permissions
   */
  async requestPermissions(): Promise<boolean> {
    try {
      const permissions = await Geolocation.requestPermissions();
      return permissions.location === 'granted';
    } catch (error: unknown) {
      if (error != null && typeof error === 'object' && Object.keys(error).length > 0) {
        console.error('Error requesting location permissions:', error);
      }
      return false;
    }
  }

  /**
   * Check if location permissions are granted (any: whenInUse or always)
   */
  async checkPermissions(): Promise<boolean> {
    try {
      const permissions = await Geolocation.checkPermissions();
      return permissions.location === 'granted';
    } catch (error: unknown) {
      if (error != null && typeof error === 'object' && Object.keys(error).length > 0) {
        console.error('Error checking location permissions:', error);
      }
      return false;
    }
  }

  /** Dernière valeur persistée par iOS (Core Location) dans UserDefaults — alignée sur le plugin natif. */
  private async readIosAuthorizationFromUserDefaults(): Promise<LocationAuthorizationStatus | null> {
    if (Capacitor.getPlatform() !== 'ios') return null;
    try {
      const ud = getUserDefaultsPlugin();
      const native = await ud.get({ key: IOS_LOCATION_AUTH_UD_KEY }) as { value?: string };
      const v = (native?.value ?? '').trim() as LocationAuthorizationStatus;
      return ['always', 'whenInUse', 'denied', 'notDetermined'].includes(v) ? v : null;
    } catch {
      return null;
    }
  }

  /** Get detailed status: always | whenInUse | denied | notDetermined */
  async getLocationAuthorizationStatus(): Promise<LocationAuthorizationStatus> {
    if (Capacitor.getPlatform() === 'web') return 'denied';
    try {
      const result = await BackgroundLocation.getLocationAuthorizationStatus();
      let s = (result?.status ?? 'denied') as LocationAuthorizationStatus;
      if (!['always', 'whenInUse', 'denied', 'notDetermined'].includes(s)) {
        s = 'denied';
      }
      // iOS: si le bridge renvoie encore notDetermined alors que Core Location est déjà résolu, lire la clé native à jour.
      if (Capacitor.getPlatform() === 'ios' && s === 'notDetermined') {
        const fromUd = await this.readIosAuthorizationFromUserDefaults();
        if (fromUd && fromUd !== 'notDetermined') {
          return fromUd;
        }
      }
      return s;
    } catch (error: unknown) {
      const err = error as { code?: string | number; message?: string };
      const errStr = typeof error === 'string' ? error : JSON.stringify(error ?? '');
      const isUnimplemented =
        err?.code === 'UNIMPLEMENTED' ||
        String(err?.code) === 'UNIMPLEMENTED' ||
        (typeof err?.message === 'string' && err.message.includes('UNIMPLEMENTED')) ||
        errStr.includes('UNIMPLEMENTED');
      if (Capacitor.getPlatform() === 'ios' && isUnimplemented) {
        try {
          // Prefer native truth source written by AppDelegate from CLLocation authorizationStatus.
          try {
            const ud = getUserDefaultsPlugin();
            const native = await ud.get({ key: IOS_LOCATION_AUTH_UD_KEY }) as { value?: string };
            const nativeStatus = (native?.value ?? '') as LocationAuthorizationStatus;
            if (['always', 'whenInUse', 'denied', 'notDetermined'].includes(nativeStatus)) {
              return nativeStatus;
            }
          } catch {
            // ignore and fallback below
          }

          const perms = await Geolocation.checkPermissions();
          // Truthful fallback: Geolocation cannot distinguish "whenInUse" vs "always" on iOS.
          // Do not guess a value here. Return "notDetermined" (unknown from JS side).
          if (perms.location === 'granted') return 'notDetermined';
          if (perms.location === 'denied') return 'denied';
          return 'notDetermined';
        } catch (_) {
          return 'denied';
        }
      }
      if (!isUnimplemented) {
        console.error('Error getting location authorization status:', error);
      }
      return 'denied';
    }
  }

  /** Open app settings → Location → Allow all the time (Step 3) */
  async openAppLocationSettings(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') return;
    await NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App,
    });
  }

  async openPermissionGuide(reason: string): Promise<boolean> {
    if (Capacitor.getPlatform() === 'web') return false;
    try {
      const result = await BackgroundLocation.openPermissionGuide({ reason });
      return result?.opened !== false;
    } catch {
      // Fallback to generic app settings entry point.
      await this.openAppLocationSettings();
      return true;
    }
  }

  async hasOverlayPermission(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return true;
    try {
      const result = await BackgroundLocation.hasOverlayPermission();
      return !!result?.granted;
    } catch {
      return false;
    }
  }

  /**
   * Start background location tracking. Returns { started, showAllowAlwaysModal }.
   * When showAllowAlwaysModal is true, caller shows explanation and calls openAppLocationSettings().
   */
  async startTracking(): Promise<StartTrackingResult> {
    if (Capacitor.getPlatform() === 'web') return { started: false };
    if (this.isTracking) {
      console.warn('[BackgroundLocation] ⚠️ Tracking déjà actif – rafraîchissement de la config native');
    }
    const status = await this.getLocationAuthorizationStatus();
    // Refus définitif : pas de nouvelle popup système fiable → guider vers Réglages (même modale + bouton).
    if (status === 'denied') {
      return { started: false, showAllowAlwaysModal: true };
    }
    if (status === 'notDetermined') {
      const granted = await this.requestPermissions();
      if (!granted) {
        console.error('[BackgroundLocation] ❌ Location permission denied by user');
        return { started: false, showAllowAlwaysModal: true };
      }
      let newStatus = await this.getLocationAuthorizationStatus();
      if (newStatus === 'always') return await this.doStartTracking();
      if (newStatus === 'whenInUse') return { started: false, showAllowAlwaysModal: true };
      if (newStatus === 'denied') return { started: false, showAllowAlwaysModal: true };
      // Race / cache: laisser le temps à iOS de persister le statut réel après la popup système.
      if (newStatus === 'notDetermined' && granted) {
        await new Promise((r) => setTimeout(r, 300));
        newStatus = await this.getLocationAuthorizationStatus();
        if (newStatus === 'always') return await this.doStartTracking();
        if (newStatus === 'whenInUse') return { started: false, showAllowAlwaysModal: true };
        if (newStatus === 'denied') return { started: false, showAllowAlwaysModal: true };
        if (Capacitor.getPlatform() === 'ios') {
          const fromUd = await this.readIosAuthorizationFromUserDefaults();
          if (fromUd === 'always') return await this.doStartTracking();
          if (fromUd === 'whenInUse' || fromUd === 'denied') {
            return { started: false, showAllowAlwaysModal: true };
          }
          // UserDefaults plugin indisponible ou statut encore flou : localisation accordée mais pas « Toujours » côté JS → ouvrir Réglages.
          if (granted) {
            return { started: false, showAllowAlwaysModal: true };
          }
        }
        if (Capacitor.getPlatform() === 'android') {
          return { started: false, showAllowAlwaysModal: true };
        }
      }
      return { started: false };
    }
    if (status === 'whenInUse') return { started: false, showAllowAlwaysModal: true };
    if (status === 'always') return await this.doStartTracking();
    return { started: false };
  }

  private async doStartTracking(): Promise<StartTrackingResult> {
    const user = this.localStorageService.getUserData();
    const role = this.roleHelper.extractRoleFromUser(user?.client);
    const token = this.localStorageService.getToken() || '';
    if (role !== 'prestataire') return { started: false };
    if (!token) return { started: false };
    try {
      // Try to use the plugin first (works on Android, might work on iOS)
      try {
        // Step 1: Set config (writes to UserDefaults on iOS / SharedPreferences on Android)
        await BackgroundLocation.setConfig({
          apiUrl: environment.authUrl,
          authToken: token,
          userRole: role
        });

        // Step 2: Start tracking
        await BackgroundLocation.startTracking();

      } catch (pluginError: any) {
        // If plugin doesn't work (UNIMPLEMENTED on iOS), use fallback method
        if (pluginError?.code === 'UNIMPLEMENTED' && Capacitor.getPlatform() === 'ios') {
          console.warn('[BackgroundLocation] ⚠️ Plugin returned UNIMPLEMENTED, but trying to execute Swift code anyway...');
          // CRITICAL: Even if Capacitor returns UNIMPLEMENTED, the Swift code might still execute
          // Try to call the methods directly - they might still write to UserDefaults
          try {
            // Try to write directly to UserDefaults using UserDefaultsPlugin first
            try {
              const UserDefaultsPlugin = getUserDefaultsPlugin();
              await UserDefaultsPlugin.set({ key: 'api_url', value: environment.authUrl });
              await UserDefaultsPlugin.set({ key: 'auth_token', value: token });
              await UserDefaultsPlugin.set({ key: 'user_role', value: role });
              await UserDefaultsPlugin.set({ key: 'native_location_command', value: this.buildCommandValue('start') });

              // Wait a bit to let AppDelegate read the command
              await new Promise(resolve => setTimeout(resolve, 500));
              this.isTracking = true;
            } catch (userDefaultsError: any) {
              // If UserDefaultsPlugin also fails, try SecureStoragePlugin
              if (userDefaultsError?.code === 'UNIMPLEMENTED') {
                console.warn('[BackgroundLocation] ⚠️ UserDefaultsPlugin unavailable, trying SecureStoragePlugin');
                throw userDefaultsError; // Will be caught by outer catch
              } else {
                throw userDefaultsError;
              }
            }
          } catch (fallbackError: any) {
            console.error('[BackgroundLocation] ❌ Error with UserDefaults fallback:', fallbackError);
            // Fallback to SecureStoragePlugin (AppDelegate can read from Keychain)
            try {
              const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
              await SecureStoragePlugin.set({ key: 'api_url', value: environment.authUrl });
              await SecureStoragePlugin.set({ key: 'auth_token', value: token });
              await SecureStoragePlugin.set({ key: 'user_role', value: role });
              await SecureStoragePlugin.set({ key: 'native_location_command', value: this.buildCommandValue('start') });
              // AppDelegate needs provider_current_state == true to start native tracking (plugin UNIMPLEMENTED on iOS)
              await SecureStoragePlugin.set({ key: 'provider_current_state', value: 'true' });
              // Wait a bit to let AppDelegate read from Keychain
              await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (secureStorageError) {
              console.error('[BackgroundLocation] ❌ All fallback methods failed:', secureStorageError);
              throw pluginError; // Re-throw original error
            }
          }
        } else {
          throw pluginError;
        }
      }

      this.isTracking = true;
      return { started: true };
    } catch (error) {
      this.isTracking = false;
      console.error('[BackgroundLocation] ❌ Error starting location tracking:', error);
      return { started: false };
    }
  }

  /**
   * Stop background location tracking
   * No-op on web: plugin is only available on native (iOS/Android).
   */
  async stopTracking(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }
    if (!this.isTracking) {
      return;
    }

    try {


      // Try to use the plugin first
      try {
        await BackgroundLocation.stopTracking();
        this.isTracking = false;
        this.lastSentLocation = null;
      } catch (pluginError: any) {
        // If plugin doesn't work (UNIMPLEMENTED on iOS), use fallback method
        if (pluginError?.code === 'UNIMPLEMENTED' && Capacitor.getPlatform() === 'ios') {
          console.warn('[BackgroundLocation] ⚠️ Plugin returned UNIMPLEMENTED, using fallback method');

          // Write "stop" command to UserDefaults/Keychain for AppDelegate to read
          try {
            const UserDefaultsPlugin = getUserDefaultsPlugin();
            await UserDefaultsPlugin.set({ key: 'native_location_command', value: this.buildCommandValue('stop') });
          } catch (userDefaultsError: any) {
            // If UserDefaultsPlugin also fails, use SecureStoragePlugin
            if (userDefaultsError?.code === 'UNIMPLEMENTED') {
              console.warn('[BackgroundLocation] ⚠️ UserDefaultsPlugin unavailable, using SecureStoragePlugin');
              try {
                const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
                await SecureStoragePlugin.set({ key: 'native_location_command', value: this.buildCommandValue('stop') });
              } catch (secureStorageError) {
                console.error('[BackgroundLocation] ❌ All fallback methods failed:', secureStorageError);
              }
            }
          }

          // Wait a bit to let native code process the command
          await new Promise(resolve => setTimeout(resolve, 500));

          this.isTracking = false;
          this.lastSentLocation = null;
        } else {
          throw pluginError;
        }
      }
    } catch (error) {
      console.error('[BackgroundLocation] ❌ Error stopping location tracking:', error);
      throw error;
    }
  }

  // Native service handles resume automatically

  /**
   * Get current location
   * Note: This method only works when app is in foreground
   * For background location tracking, use the native service (startTracking)
   */
  async getCurrentLocation(): Promise<LocationUpdate | null> {
    if (this.inBackground) {
      this.backgroundLocationAttempts++;
      if (this.backgroundLocationAttempts >= 2) {
        this.showAlwaysModalOnResume = true;
      }
    }
    try {
      // Check if app is in foreground (getCurrentPosition doesn't work in background)
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0,
      });

      const location: LocationUpdate = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy || undefined,
        altitude: position.coords.altitude || undefined,
        speed: position.coords.speed || undefined,
        heading: position.coords.heading || undefined,
        timestamp: position.timestamp || Date.now(),
      };

      return location;
    } catch (error: any) {
      // Handle specific error cases
      if (error?.code === 'OS-PLUG-GLOC-0002' || error?.message?.includes('locationUnavailable')) {
        // This is expected when app is in background - native service handles location tracking
        console.warn('[BackgroundLocation] ⚠️ Location unavailable (app may be in background)');
        console.warn('[BackgroundLocation] ℹ️ Native background tracking service handles location updates automatically');
        return null;
      }

      console.error('[BackgroundLocation] ❌ Error getting current location:', error);
      return null;
    }
  }

  /**
   * Check if tracking is active
   */
  isTrackingActive(): boolean {
    return this.isTracking;
  }

  /**
   * Vérifie si l'application est exclue de l'optimisation de batterie
   * @returns true si l'app est exclue, false sinon
   * Note: Only available on Android, iOS doesn't have battery optimization
   */
  async checkBatteryOptimization(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() === 'ios') {
      // Sur le web et iOS, toujours retourner true (pas d'optimisation)
      return true;
    }

    try {
      const result = await BackgroundLocation.checkBatteryOptimization();
      return result.isIgnoring;
    } catch (error) {
      console.error('[BackgroundLocation] Error checking battery optimization:', error);
      return false;
    }
  }

  /**
   * Demande à l'utilisateur de désactiver l'optimisation de batterie
   * Ouvre les paramètres système pour permettre à l'utilisateur de désactiver l'optimisation
   * Note: Only available on Android, iOS doesn't have battery optimization
   */
  async requestBatteryOptimizationDisable(): Promise<void> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() === 'ios') {
      console.warn('[BackgroundLocation] Battery optimization is not applicable on web/iOS');
      return;
    }

    try {
      await BackgroundLocation.requestBatteryOptimizationDisable();
    } catch (error) {
      console.error('[BackgroundLocation] Error requesting battery optimization disable:', error);
      throw error;
    }
  }

  /**
   * Sync auth config to native prefs (required before presence ping).
   */
  async applyNativeAuthConfig(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }
    const user = this.localStorageService.getUserData();
    const role = this.roleHelper.extractRoleFromUser(user?.client);
    const token = this.localStorageService.getToken() || '';
    if (!token || role !== 'prestataire') {
      return;
    }
    const currentState = user?.client?.current_state === true;
    try {
      await BackgroundLocation.setConfig({
        apiUrl: environment.authUrl,
        authToken: token,
        userRole: role,
        providerCurrentState: currentState,
      });
    } catch (error) {
      console.warn('[BackgroundLocation] applyNativeAuthConfig failed:', error);
    }
  }

  /**
   * Start native prestataire presence heartbeat (foreground + background).
   */
  async startPresencePing(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }
    await this.applyNativeAuthConfig();
    try {
      await BackgroundLocation.startPresencePing();
      console.log('[BackgroundLocation] Native presence ping started');
    } catch (error) {
      console.warn('[BackgroundLocation] startPresencePing failed:', error);
    }
  }

  /**
   * Stop native prestataire presence heartbeat.
   */
  async stopPresencePing(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }
    try {
      await BackgroundLocation.stopPresencePing();
      console.log('[BackgroundLocation] Native presence ping stopped');
    } catch (error) {
      console.warn('[BackgroundLocation] stopPresencePing failed:', error);
    }
  }
}

